import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { METRICS_COLUMNS } from "../metricsColumns";
import type { MetricsSort, MetricsSortColumn } from "../metricsColumns";
import { MetricsCellContent } from "./MetricsCellContent";
import type { PartnerMetricsRow } from "../metrics.types";

interface PartnerMetricsTableProps {
  rows: PartnerMetricsRow[];
  sort: MetricsSort;
  onSort: (column: MetricsSortColumn) => void;
  onSelectPartner: (id: string) => void;
}

/**
 * The partner metrics table — a typed port of the POC's `renderMetricsTable`.
 * Headers + sort come from `METRICS_COLUMNS`; the body formats each cell
 * explicitly (per-column formatting is inherent). A row click selects that
 * partner (`onMetricsRowClick`/`goToPartnerFromMetrics` in the POC) — the
 * `window.getSelection()` guard matches the POC's own: clicking to select/
 * copy cell text shouldn't also trigger navigation.
 */
export function PartnerMetricsTable({
  rows,
  sort,
  onSort,
  onSelectPartner,
}: PartnerMetricsTableProps) {
  const handleRowClick = (id: string) => {
    if (window.getSelection()?.toString()) return;
    onSelectPartner(id);
  };

  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small" aria-label="Partner metrics">
        <TableHead>
          <TableRow>
            {METRICS_COLUMNS.map((column) => (
              <TableCell
                key={column.id}
                align={column.numeric ? "right" : "left"}
                sortDirection={sort.column === column.id ? sort.direction : false}
              >
                <TableSortLabel
                  active={sort.column === column.id}
                  direction={sort.column === column.id ? sort.direction : "asc"}
                  onClick={() => onSort(column.id)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={METRICS_COLUMNS.length}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No partners match the current filters.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                onClick={() => handleRowClick(row.id)}
                sx={{ cursor: "pointer" }}
              >
                {METRICS_COLUMNS.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.numeric ? "right" : "left"}
                    sx={column.id === "overage" ? { whiteSpace: "nowrap" } : undefined}
                  >
                    <MetricsCellContent cell={column.project(row)} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
